import React, {
  useCallback, useEffect, useReducer, useRef, useState,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import {
  Box, Button, Divider, List, ListItem, ListItemText, Stack, Typography,
} from '@mui/material';
import {
  createUserGroup,
  deleteUserGroup,
  getUserGroup,
  searchUserGroups,
  updateUserGroup,
} from 'shared/api/admin';
import { isValidICSEmail } from 'shared/utils';
import { BUTTON_COLORS, VARIANTS } from 'shared/constants';
import { DataFormatter } from 'shared/components/data-display/DataFormatter';
import { useAuth, useRequest } from 'shared/hooks';
import { TextEditor, withDataValidator } from 'shared/components/data-display/editors';
import { InfoModal, Title } from 'shared/components/abstract/interactables';
import { AsyncSearchableSelect } from 'shared/components/select/SearchableSelects';
import { dataReducer, DataReducerActions, initialDataReducerState } from 'shared/reducers';
import { UserResourceGrid } from './styled';
import UserGroupEmployees from './UserGroupEmployees';

export default function UserGroup() {
  const {
    request,
    isLoading,
  } = useRequest();
  const {
    push: pushHistory,
    replace: replaceHistory,
  } = useHistory();
  const { search } = useLocation();

  const {
    isAdmin,
  } = useAuth();

  const [state, dispatch] = useReducer(dataReducer, initialDataReducerState);
  const [disableSave, setDisableSave] = useState(false);
  const [updateCount, setUpdateCount] = useState(0);
  const [employeesToAdd, setEmployeesToAdd] = useState([]);
  const employeesRef = useRef(null);

  const {
    data: userGroup,
    updatedData,
  } = state;

  // syncs the id in the url to the user group
  useEffect(() => {
    const id = parseInt(new URLSearchParams(search).get('id'), 10);
    // if reloading after a change and the user group has not changed then return
    if (!userGroup || !(userGroup.id === id || userGroup.staged)) {
      setEmployeesToAdd([]);
      if (!id) {
        dispatch({
          action: DataReducerActions.set,
          payload: null,
        });
        replaceHistory('?');
      } else {
        (async () => {
          if (id && isAdmin) {
            // if reloading after a change and the user group has not changed
            // but an employee update call has been made then get the updated user group
            const result = await request(getUserGroup(id));
            if (result) {
              dispatch({
                action: DataReducerActions.set,
                payload: result,
              });
            } else {
              pushHistory('?');
            }
          }
        })();
      }
    }
  }, [request, search, userGroup, pushHistory, replaceHistory, isAdmin]);

  /**
   * Validate the update state can be saved
   * 1. Must have a GroupName
   * 1. Must have valid Email
   */
  useEffect(() => {
    const emailUpdate = updatedData?.email;
    const groupNameUpdate = updatedData?.group_name;
    if (groupNameUpdate !== undefined && !groupNameUpdate) {
      setDisableSave(true);
      return;
    }
    if (emailUpdate && !isValidICSEmail(updatedData?.email)) {
      setDisableSave(true);
      return;
    }

    setDisableSave(false);
  }, [updatedData]);

  const setResource = useCallback((newUserGroup) => {
    if (!newUserGroup) {
      dispatch({
        action: DataReducerActions.set,
        payload: null,
      });
      pushHistory('?');
    } else {
      // sets the new resource to the current state and pushes history
      // when creating a new user group, set the group name to the name given
      if (newUserGroup.staged) {
        dispatch({
          action: DataReducerActions.set,
          payload: {
            group_name: newUserGroup.name,
            email: '',
            staged: true,
          },
        });
      } else {
        dispatch({
          action: DataReducerActions.set,
          payload: { ...newUserGroup, staged: false },
        });
      }
      pushHistory(newUserGroup.id === undefined ? '?' : `?id=${newUserGroup.id}`);
    }
  }, [pushHistory]);

  const onDelete = async () => {
    const result = await request(deleteUserGroup(userGroup.id), 'Deleted user group');
    if (result) {
      dispatch({
        action: DataReducerActions.set,
        payload: null,
      });
      setUpdateCount(updateCount + 1);
      pushHistory('?');
    }
  };

  const onSave = async () => {
    if (Object.keys(updatedData).length > 0) {
      const newData = { ...userGroup, ...updatedData };
      const result = await request(updateUserGroup(userGroup.id, newData), 'Updated');
      if (result) {
        dispatch({
          action: DataReducerActions.set,
          payload: result,
        });
        setUpdateCount(updateCount + 1);
      }
    }
    return null;
  };

  const onClear = () => {
    dispatch({ action: DataReducerActions.clear });
  };

  const createGroup = async () => {
    if (Object.keys(updatedData).length > 0 && isValidICSEmail(updatedData.email)) {
      const newData = {
        ...userGroup,
        ...updatedData,
      };
      const result = await request(createUserGroup(newData), 'Created User Group');
      if (result && employeesRef.current) {
        const employeeAddResult = await employeesRef.current.addEmployees(result.id);
        if (employeeAddResult || result) {
          dispatch({
            action: DataReducerActions.set,
            payload: employeeAddResult || result,
          });
        }
        setUpdateCount(updateCount + 1);
        pushHistory(`?id=${result.id}`);
      }
    }
  };

  const userGroupDescriptions = [
    {
      editInline: true,
      editor: TextEditor,
      key: 'group_name',
      label: 'Group Name',
      required: true,
      readOnly: !isAdmin,
    },
    {
      label: 'Email',
      key: 'email',
      editInline: true,
      editor: TextEditor,
      validator: withDataValidator({
        validatorFunc: isValidICSEmail,
        helperText: 'Use a valid ICS email',
      }),
      required: true,
      formHelperTextProps: {
        style: {
          backgroundColor: 'subtleBackground',
          margin: 0,
        },
      },
      readOnly: !isAdmin,
    },
  ];

  return (
    <Grid container spacing={1}>
      <Title title="ICS User Groups" />

      <Grid size={{
        xs: 8,
        lg: 3,
      }}
      >
        <AsyncSearchableSelect
          creatable
          label="User Groups"
          onChange={setResource}
          searchRequest={searchUserGroups}
          selectedOption={userGroup}
          getOptionLabel={(r) => r.group_name || r.name}
          updateCount={updateCount}
        />
      </Grid>
      <Grid size={12}>
        <Divider />
      </Grid>
      {userGroup
        && (
          <UserResourceGrid
            container
            size={12}
            sx={(theme) => ({
              [theme.breakpoints.down('lg')]: {
                width: '100%',
              },
            })}
          >
            <Grid size={{
              xs: 12,
              lg: 5,
            }}
            >
              <strong>Group Information</strong>
              <DataFormatter
                data={userGroup}
                descriptions={userGroupDescriptions}
                updateValue={(key, value) => dispatch({
                  action: 'update',
                  payload: {
                    key,
                    value,
                  },
                })}
                updatedData={updatedData}
              />
              {!userGroup.staged && isAdmin && state.dirty && (
                <Stack direction="row" spacing={1.25} sx={{ justifyContent: 'flex-end' }}>
                  <Button
                    disabled={!state.dirty}
                    onClick={onClear}
                    variant={VARIANTS.outlined}
                  >
                    Undo
                  </Button>
                  <InfoModal
                    title="Save Group"
                    actionName="Save"
                    description={
                      <p>{`This will update "${userGroup.group_name}". Are you sure?`}</p>
                    }
                    actionCallback={onSave}
                    color={BUTTON_COLORS.primary}
                    disabled={disableSave}
                  />
                </Stack>
              )}
              {!userGroup.staged && !state.dirty
                && (
                  <Stack direction="row" sx={{ justifyContent: 'flex-end' }}>
                    <InfoModal
                      title="Delete Group"
                      actionName="Delete Group"
                      description={
                        <p>{`This will delete "${userGroup.group_name}". Are you sure?`}</p>
                      }
                      actionCallback={onDelete}
                      color={BUTTON_COLORS.error}
                    />
                  </Stack>
                )}
              {userGroup.staged && isAdmin && employeesRef.current && (
                <Stack direction="row" sx={{ justifyContent: 'flex-end' }}>
                  <InfoModal
                    title="Create Group"
                    actionName="Create"
                    description={(
                      <Box>
                        <Typography>
                          This will create a new user group
                          {` "${updatedData.group_name || userGroup.group_name}"`}
                        </Typography>
                        <Box sx={{ my: 1 }}>
                          With employees:
                          <List
                            sx={{
                              listStyleType: 'disc',
                              pl: 4,
                            }}
                            dense
                          >
                            {employeesToAdd.map((employee) => (
                              <ListItem
                                key={employee.employeeId}
                                disablePadding
                                sx={{ display: 'list-item' }}
                              >
                                <ListItemText primary={employee.fullname} />
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                        <Typography>
                          Are you sure?
                        </Typography>
                      </Box>
                    )}
                    actionCallback={createGroup}
                    color={BUTTON_COLORS.primary}
                    disabled={isLoading || disableSave}
                  />
                </Stack>
              )}
            </Grid>
            <Divider orientation="vertical" variant="middle" flexItem />
            <Grid size={{
              xs: 12,
              lg: 6,
            }}
            >
              <strong>Employees</strong>
              <UserGroupEmployees
                userGroupId={userGroup.id ?? null}
                staged={userGroup.staged}
                groupName={userGroup.group_name}
                groupEmployees={userGroup.user_group_employees || []}
                onEmployeesToAddChange={setEmployeesToAdd}
                ref={employeesRef}
              />
            </Grid>
          </UserResourceGrid>
        )}
    </Grid>
  );
}
